import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Tooltip,
  useToast,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaBug } from "react-icons/fa";

import CodesetSelect from "./CodesetSelect";
import PlacingStepper from "./PlacingStepper";
import CodexLookupText from "./CodexLookupText";
import { getSignalPayload } from "./utils/getSignalPayload";
import DocumentDefinitionSelectById from "./DocumentDefinitionSelectById";
import { useAuth } from "./AuthContext";

const jsonEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const getIdish = (val) => {
  if (!val) return null;
  if (typeof val === "string") return val;
  return val._id ?? val.id ?? val.value ?? null;
};

const ContractInstanceEditor = ({
  productCode,
  contractInstance,
  productInstance,
  onSave,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const [debugMode, setDebugMode] = useState(false);

  const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
  const [docRepoCache, setDocRepoCache] = useState({}); // { [repoId]: { name, createdDate } }
  const [creatingDocIndex, setCreatingDocIndex] = useState(null);

  // Template selection modal state
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [productTemplates, setProductTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const documentsGridRef = useRef(null);

  // Delete confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const cancelDeleteRef = useRef(null);

  const [form, setForm] = useState(contractInstance || {});
  const [isDirty, setIsDirty] = useState(false);
  const [serverSnapshot, setServerSnapshot] = useState(contractInstance || {});
  const [hasRemoteUpdate, setHasRemoteUpdate] = useState(false);

  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);

  // track "active editing" to pause polling while user is typing/focused
  const editingCountRef = useRef(0);
  const isEditing = () => editingCountRef.current > 0;

const openDocumentEditor = (doc) => {
  // Require a linked repo so the editor can load from Documents API

  if (debugMode) console.log("Opening document editor for", doc);
  if (!doc?.documentRepositoryId) {
    toast({
      title: "No linked document",
      description: "Create & link the document first, then edit.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  if (debugMode) console.log("Navigating to document editor for repo id", doc.id);
  // Navigate to the loader; it will pick the doc via ?doc=<documentId>
  navigate(`/editor/${productCode}/${form._id}?doc=${doc.id}`);
};

  // === Keep local form in sync when parent sends a new instance ===
  useEffect(() => {
    setForm(contractInstance || {});
    setServerSnapshot(contractInstance || {});
    setIsDirty(false);
    setHasRemoteUpdate(false);
  }, [contractInstance]);

  // === Fetch registers ===
  useEffect(() => {
    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        if (!res.ok) throw new Error("Failed to fetch registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
        if (debugMode) console.error("❌ Error fetching registers", err);
      } finally {
        setRegisterLoading(false);
      }
    };
    fetchRegisters();
  }, []);

  // === Global focus listeners to pause polling ===
  useEffect(() => {
    const onFocusIn = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tag) ||
        e.target?.isContentEditable
      ) {
        editingCountRef.current += 1;
      }
    };
    const onFocusOut = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tag) ||
        e.target?.isContentEditable
      ) {
        editingCountRef.current = Math.max(0, editingCountRef.current - 1);
      }
    };
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // === Warn on unload if dirty ===
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // === Helpers ===
  const markDirty = (updater) => {
    setForm((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!jsonEqual(next, prev)) setIsDirty(true);
      return next;
    });
  };

  const handleChange = (key, value) => {
    markDirty((prev) => ({ ...prev, [key]: value }));
  };

  // ----- Document helpers -----
  // Merge-friendly: can accept an id string, a doc object, or a { definitionId } shape.
  const handleDocumentChange = (docIndex, newVal) => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? [...prev.documents] : [];
      if (docIndex < 0 || docIndex >= docs.length) return prev;

      const current = docs[docIndex] || {};
      let patch = {};

      // If it's a string or selection object, treat as a definition change
      const tryId = getIdish(newVal);
      if (tryId) {
        patch.definitionId = tryId;
        patch.selectedDefinitionId = tryId;
      }

      // If caller passed a doc-like object with fields (e.g., { status: 'draft' })
      if (newVal && typeof newVal === "object" && !tryId) {
        patch = { ...patch, ...newVal };
      }

      docs[docIndex] = { ...current, ...patch };
      return { ...prev, documents: docs };
    });
  };

  const handleDocumentFieldChange = (docIndex, field, value) => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? [...prev.documents] : [];
      if (docIndex < 0 || docIndex >= docs.length) return prev;
      const current = docs[docIndex] || {};
      docs[docIndex] = { ...current, [field]: value };
      return { ...prev, documents: docs };
    });
  };

  const handleAddDocument = () => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? prev.documents : [];
      const newDocument = {
        id:
          (typeof crypto !== "undefined" &&
            crypto?.randomUUID &&
            crypto.randomUUID()) ||
          `${Date.now()}-${Math.random()}`,
        status: "",
        definitionId: null,
        selectedDefinitionId: null,
      };
      return { ...prev, documents: [...docs, newDocument] };
    });
  };

  const handleRemoveDocument = (docIndex) => {
    const docs = Array.isArray(form?.documents) ? form.documents : [];
    const doc = docs[docIndex];
    setDocumentToDelete({ index: docIndex, doc });
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = () => {
    if (documentToDelete !== null) {
      markDirty((prev) => {
        const docs = Array.isArray(prev.documents) ? prev.documents : [];
        const docIndex = documentToDelete.index;
        if (docIndex < 0 || docIndex >= docs.length) return prev;
        const nextDocs = docs.filter((_, i) => i !== docIndex);
        return { ...prev, documents: nextDocs };
      });
      toast({
        title: "Document removed",
        status: "success",
        duration: 2000,
      });
    }
    setIsDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };
  // ----------------------------

  // === Poll the contract for live updates (but be polite) ===
  useEffect(() => {
    if (!contractInstance?._id || !productCode) return;

    const tick = async () => {
      // Pause polling if user is actively editing or tab hidden
      if (document.visibilityState === "hidden" || isEditing()) return;

      try {
        const res = await fetch(
          `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`
        );
        if (!res.ok) throw new Error("Failed to refresh contract data");
        const updated = await res.json();

        if (isDirty) {
          // Don't overwrite local edits — just store a fresh snapshot and notify
          setServerSnapshot(updated);
          setHasRemoteUpdate(true);
          return;
        }

        // Only adopt if different
        if (!jsonEqual(updated, form)) {
          setForm(updated);
          setServerSnapshot(updated);
          setHasRemoteUpdate(false);
        }
      } catch (err) {
        if (debugMode) console.warn("Polling failed:", err.message);
      }
    };

    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractInstance?._id, productCode, isDirty, form]);

  useEffect(() => {
    const docs = Array.isArray(form?.documents) ? form.documents : [];
    docs
      .map((d) => d?.documentRepositoryId)
      .filter((id) => !!id && !docRepoCache[id])
      .forEach((id) => hydrateRepoDocName(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.documents]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(
        `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            // "If-Match": form._etag ?? undefined, // if your API supports optimistic concurrency
          },
          body: JSON.stringify(form),
        }
      );
      if (debugMode) console.log("Save response:", res);
      if (debugMode) console.log("Saved payload:", JSON.stringify(form, null, 2));
      if (!res.ok) throw new Error("Failed to update contract");

      const saved = (await res.json().catch(() => null)) || form;

      setServerSnapshot(saved);
      setForm(saved);
      setIsDirty(false);
      setHasRemoteUpdate(false);

      toast({ title: "Contract saved", status: "success" });
      onSave?.(saved);
    } catch (err) {
      if (debugMode) console.error("❌ Error saving contract", err);
      toast({ title: "Error saving contract", status: "error" });
    }
  };

  const handleSignal = (signal, control) => {
    switch (signal) {
      case "contractBound":
        getSignalPayload(control.registerType, control.entryType)
          .then((payload) => {
            if (!payload) {
              toast({
                title: "No signal payload",
                description: "Please load a signal first.",
                status: "warning",
              });
              return;
            }
            payload.type = "contractBound";
            payload.data[0].references = [
              { type: "contract_id", value: contractInstance._id },
            ];

            if (debugMode) console.log("Sending signal payload:", JSON.stringify(payload, null, 2));

            fetch(
              `https://poc.mrc.ai/${productCode}/api/signals/standard-signal`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload, null, 2),
              }
            )
              .then(async (res) => {
                if (!res.ok) throw new Error("Failed to send signal");
                return res.headers
                  .get("content-type")
                  ?.includes("application/json")
                  ? res.json()
                  : {};
              })
              .then(() => {
                toast({ title: "Signal sent successfully", status: "success" });
              })
              .catch((err) => {
                if (debugMode) console.error("❌ Error sending signal", err);
                toast({
                  title: "Error sending signal",
                  description: err.message,
                  status: "error",
                });
              });
          })
          .catch((err) => {
            toast({
              title: "Error loading signal",
              description: err.message,
              status: "error",
            });
          });
        break;

      case "claimMovement":
        getSignalPayload(control.registerType, control.entryType)
          .then((payload) => {
            if (!payload) {
              toast({
                title: "No signal payload",
                description: "Please load a signal first.",
                status: "warning",
              });
              return;
            }
            
            payload.type = "claimMovement";
            payload.data[0].references = [
              { type: "contract_id", value: contractInstance._id }
            ];

            if (debugMode) {
              console.log("Sending claim movement signal:", JSON.stringify(payload, null, 2));
            }

            return fetch(
              `https://poc.mrc.ai/${productCode}/api/signals/standard-signal`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }
            );
          })
          .then(async (res) => {
            if (!res.ok) throw new Error("Failed to send signal");
            return res.headers.get("content-type")?.includes("application/json")
              ? res.json()
              : {};
          })
          .then((result) => {
            toast({ 
              title: "Claim submitted successfully", 
              description: "Claims Agreement workflow initiated",
              status: "success" 
            });
            
            if (debugMode && result.workflowInstanceId) {
              console.log("Workflow instance created:", result.workflowInstanceId);
            }
          })
          .catch((err) => {
            console.error("❌ Error sending claim signal", err);
            toast({
              title: "Error sending claim signal",
              description: err.message,
              status: "error",
            });
          });
        break;

      default:
        toast({
          title: "Unknown signal",
          description: `Unhandled signal type: ${signal}`,
          status: "warning",
        });
    }
  };

  // GET a document by id (best effort) and cache its name
  const hydrateRepoDocName = async (repoId) => {
    if (!repoId || docRepoCache[repoId]) return;
    try {
      const res = await fetch(`${DOCS_API}/${repoId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Failed to read document");
      const doc = await res.json();
      setDocRepoCache((m) => ({
        ...m,
        [repoId]: { name: doc?.name ?? repoId, createdDate: doc?.createdDate },
      }));
    } catch {
      // fallback to id as name if GET is not available
      setDocRepoCache((m) => ({ ...m, [repoId]: { name: repoId } }));
    }
  };

  // Update document name in the repository with save coordination
  const updateDocumentName = async (repoId, newName) => {
    if (!repoId || !newName) return;

    console.log("[ContractInstanceEditor] Updating document name:", {
      repoId,
      newName
    });

    try {
      // First, fetch the current document to get all its fields
      const getRes = await fetch(`${DOCS_API}/${repoId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!getRes.ok) throw new Error("Failed to fetch document");
      const currentDoc = await getRes.json();

      console.log("[ContractInstanceEditor] Current document fetched:", {
        repoId,
        currentName: currentDoc?.name,
        hasContent: !!currentDoc?.content,
        hasCodifiedContents: !!currentDoc?.codifiedContents
      });

      // Resolve user ID from various possible fields
      const userId = user?._id ||
                     user?.platformUser?._id ||
                     user?.platformUser?.id ||
                     user?.linkedInId ||
                     user?.sub ||
                     user?.email ||
                     null;

      // Now update with the new name, preserving all other fields
      const updatePayload = {
        ...currentDoc,
        name: newName,
        updatedBy: userId,
        updatedDate: new Date().toISOString(),
      };

      console.log("[ContractInstanceEditor] Sending update:", {
        repoId,
        newName,
        payloadSize: JSON.stringify(updatePayload).length
      });

      const res = await fetch(`${DOCS_API}/${repoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatePayload),
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error("[ContractInstanceEditor] Update failed:", {
          status: res.status,
          errorText
        });
        throw new Error(errorText || "Failed to update document name");
      }

      const updated = await res.json();

      console.log("[ContractInstanceEditor] Document name updated successfully:", {
        repoId,
        newName: updated?.name
      });

      // Update cache
      setDocRepoCache((m) => ({
        ...m,
        [repoId]: { name: updated?.name ?? newName, createdDate: updated?.createdDate },
      }));

      toast({
        title: "Document name updated",
        status: "success",
        duration: 2000,
      });
    } catch (err) {
      console.error("[ContractInstanceEditor] Update document name error:", err);
      if (debugMode) console.error("❌ Update document name error", err);
      toast({
        title: "Error updating document name",
        description: err.message,
        status: "error",
      });
    }
  };

  // POST create + link a document for this row, using its definitionId
  const createAndLinkDocument = async (docIndex) => {
    const docs = Array.isArray(form?.documents) ? form.documents : [];
    const row = docs[docIndex];
    const definitionId = row?.definitionId ?? row?.selectedDefinitionId;

    if (!definitionId) {
      toast({ title: "Pick a document type first", status: "warning" });
      return;
    }

    try {
      setCreatingDocIndex(docIndex);

      // Resolve user ID from various possible fields
      const userId = user?._id ||
                     user?.platformUser?._id ||
                     user?.platformUser?.id ||
                     user?.linkedInId ||
                     user?.sub ||
                     user?.email ||
                     null;

      const res = await fetch(DOCS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          definitionId,
          name: "New Document",
          createdBy: userId,
        }),
      });
      if (!res.ok) throw new Error("Failed to create document");

      const created = await res.json(); // { _id, definitionId, name, content, createdDate }

      // 1) cache the name so we can display immediately
      setDocRepoCache((m) => ({
        ...m,
        [created._id]: { name: created.name, createdDate: created.createdDate },
      }));

      // 2) Build the updated form directly (don't rely on async state updates)
      const updatedForm = { ...form };
      const updatedDocs = Array.isArray(updatedForm.documents) ? [...updatedForm.documents] : [];
      if (docIndex >= 0 && docIndex < updatedDocs.length) {
        updatedDocs[docIndex] = {
          ...updatedDocs[docIndex],
          documentRepositoryId: created._id,
          status: "new"
        };
        updatedForm.documents = updatedDocs;
      }

      // 3) Auto-save the contract instance to persist the document link
      try {
        const saveRes = await fetch(
          `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedForm),
          }
        );

        if (!saveRes.ok) throw new Error("Failed to auto-save contract");

        const saved = (await saveRes.json().catch(() => null)) || updatedForm;
        setServerSnapshot(saved);
        setForm(saved);
        setIsDirty(false);
        setHasRemoteUpdate(false);

        toast({
          title: "Document created & linked",
          description: `${created.name} - Contract saved automatically`,
          status: "success",
          duration: 3000,
        });

        onSave?.(saved);
      } catch (saveErr) {
        if (debugMode) console.error("❌ Auto-save error", saveErr);
        toast({
          title: "Document created but not saved",
          description: "Please save the contract manually to persist the link",
          status: "warning",
          duration: 5000,
        });
      }
    } catch (err) {
      if (debugMode) console.error("❌ Create document error", err);
      toast({
        title: "Error creating document",
        description: err.message,
        status: "error",
      });
    } finally {
      setCreatingDocIndex(null);
    }
  };

  // Fetch product templates for the template selection modal
  const fetchProductTemplates = async () => {
    try {
      const params = new URLSearchParams({
        page: "1",
        pageSize: "100",
        sortBy: "name",
        sortOrder: "asc",
      });

      // Filter by productCode and isTemplate
      if (productCode) {
        params.append("productCode", productCode);
      }
      params.append("isTemplate", "true");

      const res = await fetch(`${DOCS_API}?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch templates");
      
      const data = await res.json();
      setProductTemplates(data.items || []);
    } catch (err) {
      console.error("Error fetching product templates:", err);
      toast({
        title: "Error loading templates",
        description: err.message,
        status: "error",
      });
    }
  };

  // Create new document from template
  const handleCreateFromTemplate = async () => {
    if (!selectedTemplateId) {
      toast({ title: "Please select a template", status: "warning" });
      return;
    }

    try {
      // Call the copy endpoint
      const res = await fetch(`${DOCS_API}/${selectedTemplateId}/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Failed to copy template");

      const copiedDoc = await res.json();

      // Add the new document to the contract's documents array
      const updatedDocs = Array.isArray(form?.documents) ? [...form.documents] : [];
      updatedDocs.push({
        definitionId: copiedDoc.definitionId,
        documentRepositoryId: copiedDoc._id,
        status: copiedDoc.status || "new",
      });

      const updatedForm = { ...form, documents: updatedDocs };

      // Auto-save the contract to persist the link
      try {
        const saveRes = await fetch(
          `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedForm),
          }
        );

        if (!saveRes.ok) throw new Error("Failed to save contract");

        const saved = (await saveRes.json().catch(() => null)) || updatedForm;
        setServerSnapshot(saved);
        setForm(saved);
        setIsDirty(false);

        // Update cache with the new document name
        setDocRepoCache((m) => ({
          ...m,
          [copiedDoc._id]: { name: copiedDoc.name, createdDate: copiedDoc.createdDate },
        }));

        toast({
          title: "Document created from template",
          description: `${copiedDoc.name} has been added`,
          status: "success",
        });

        // Close modal and reset selection
        setIsTemplateModalOpen(false);
        setSelectedTemplateId(null);
      } catch (saveErr) {
        console.error("Auto-save error:", saveErr);
        toast({
          title: "Document created but not saved",
          description: "Please save the contract manually",
          status: "warning",
        });
      }
    } catch (err) {
      console.error("Error creating from template:", err);
      toast({
        title: "Error creating document",
        description: err.message,
        status: "error",
      });
    }
  };

  const handleOpenTemplateModal = () => {
    fetchProductTemplates();
    setIsTemplateModalOpen(true);
  };

  // AG Grid cell renderers for documents tab
  const EditDocCellRenderer = (params) => {
    const doc = params.data;
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Edit Document">
          <IconButton
            icon={<HiOutlinePencil />}
            size="sm"
            onClick={() => openDocumentEditor(doc)}
            isDisabled={!doc?.documentRepositoryId}
            aria-label="Edit Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const DeleteDocCellRenderer = (params) => {
    const docIndex = params.node.rowIndex;
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Remove Document">
          <IconButton
            icon={<HiOutlineTrash />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => handleRemoveDocument(docIndex)}
            aria-label="Remove Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const DocumentNameCellRenderer = (params) => {
    const doc = params.data;
    const repoId = doc?.documentRepositoryId;
    const repoName = repoId ? docRepoCache[repoId]?.name : "";
    return repoName || "Not linked";
  };

  const StatusCellRenderer = (params) => {
    const doc = params.data;
    return doc.status || "—";
  };

  const TypeCellRenderer = (params) => {
    const doc = params.data;
    const [definitionName, setDefinitionName] = useState("Loading...");

    useEffect(() => {
      const fetchDefinitionName = async () => {
        const definitionId = doc.definitionId ?? doc.selectedDefinitionId;
        if (!definitionId) {
          setDefinitionName("No type set");
          return;
        }

        try {
          const res = await fetch(`https://documents.poc.mrc.ai/api/definitions/${definitionId}`);
          if (!res.ok) throw new Error("Failed to fetch");
          const definition = await res.json();
          setDefinitionName(definition.name || "Unnamed");
        } catch (err) {
          console.error("Error fetching definition:", err);
          setDefinitionName("Unknown");
        }
      };

      fetchDefinitionName();
    }, [doc.definitionId, doc.selectedDefinitionId]);

    return <Text>{definitionName}</Text>;
  };

  // AG Grid column definitions for documents
  const documentColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "edit",
        cellRenderer: EditDocCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
        pinned: "left",
      },
      {
        headerName: "Document Name",
        field: "documentRepositoryId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: DocumentNameCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "definitionId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: TypeCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        sortable: true,
        filter: true,
        cellRenderer: StatusCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteDocCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
    ],
    [docRepoCache, creatingDocIndex]
  );

  const documentDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    }),
    []
  );

  return (
    <Box p={6} maxWidth="100%" mx="auto">
      <HStack>
        <Tooltip
          label="Back to the Product Dashboard"
          aria-label="Product Dashboard tooltip">
          <IconButton
            icon={<HiOutlineArrowNarrowLeft />}
            size="md"
            onClick={() => window.history.back()}
          />
        </Tooltip>
        <Heading size="lg">{form?.name || contractInstance?.name}</Heading>
        <Tooltip
          label="Save Contract Instance"
          aria-label="Save Contract Instance tooltip">
          <IconButton
            icon={<HiOutlineSave />}
            size="md"
            onClick={handleSubmit}
          />
        </Tooltip>
        {isSuperAdmin && (
          <Tooltip label={debugMode ? "Disable Debug Mode" : "Enable Debug Mode"}>
            <IconButton
              icon={<FaBug />}
              aria-label="Toggle Debug Mode"
              onClick={() => setDebugMode(!debugMode)}
              variant={debugMode ? "solid" : "ghost"}
              colorScheme={debugMode ? "red" : undefined}
            />
          </Tooltip>
        )}
      </HStack>

      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        borderColor="gray.200"
        backgroundColor="whiteAlpha.800"
        mt={4}>
        <PlacingStepper
          showSubSteps={false}
          size="lg"
          stageCode={form?.placingStageCode}
          transactionCode={form?.placingTransactionFunctionCode}
          defaultOrientation="horizontal"
        />
      </Box>

      {/* Controls */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        mt={4}
        borderColor="gray.200"
        backgroundColor="whiteAlpha.800">
        <Table variant="simple" size="sm">
          <Tbody>
            {Array.isArray(productInstance?.controls) &&
            productInstance.controls.length > 0 ? (
              productInstance.controls.map((control, index) => {
                const register = registers.find(
                  (r) => r._id === control.registerType
                );
                const entry = register?.entries?.find(
                  (e) => e.id === control.entryType
                );
                return (
                  <Tr key={index}>
                    <Td whiteSpace="nowrap" w="1%">
                      <HStack spacing={1}>
                        <Button
                          size="sm"
                          colorScheme="linkedin"
                          onClick={() => handleSignal(control.signal, control)}>
                          {control.name || entry?.name || "Unnamed Control"}
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td>No contract controls defined.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        borderColor="gray.500"
        backgroundColor="whiteAlpha.800"
        mt={4}>
        <Tabs variant="line" colorScheme="linkedin">
          <TabList>
            <Tab>Header</Tab>
            <Tab>Documents</Tab>
            <Tab>Semaphores</Tab>
            <Tab>Roles</Tab>
            <Tab>Activities</Tab>
            <Tab>Logs</Tab>
          </TabList>
          <TabPanels>
            {/* Header */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                <Box>
                  <Stack spacing={4}>
                    <FormControl>
                      <FormLabel>Id</FormLabel>
                      <FormLabel>
                        {form?._id || contractInstance?._id || ""}
                      </FormLabel>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Contract Name</FormLabel>
                      <Input
                        value={form?.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    </FormControl>

                    <CodesetSelect
                      label="Type"
                      codesetCode="MRCAI001"
                      value={
                        form?.type ? { code: form.type, description: "" } : null
                      }
                      onChange={(value) =>
                        handleChange("type", value?.code ?? "")
                      }
                    />

                    <CodesetSelect
                      label="Placing Stage"
                      codesetCode="A83"
                      filter="#Placing"
                      value={
                        form?.placingStageCode
                          ? { code: form.placingStageCode, description: "" }
                          : null
                      }
                      onChange={(value) =>
                        handleChange("placingStageCode", value?.code ?? "")
                      }
                    />

                    <CodesetSelect
                      label="Placing Transaction"
                      codesetCode="1225"
                      filter="#Placing"
                      value={
                        form?.placingTransactionFunctionCode
                          ? {
                              code: form.placingTransactionFunctionCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        handleChange(
                          "placingTransactionFunctionCode",
                          value?.code ?? ""
                        )
                      }
                    />
                  </Stack>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Documents */}
            <TabPanel>
              <HStack mb={4}>
                <Button
                  leftIcon={<HiOutlinePlus />}
                  colorScheme="blue"
                  onClick={handleOpenTemplateModal}
                >
                  Create from Template
                </Button>
              </HStack>

              {form.documents && form.documents.length > 0 ? (
                <Box className="ag-theme-quartz" style={{ height: "400px", width: "100%" }}>
                  <AgGridReact
                    ref={documentsGridRef}
                    rowData={form.documents}
                    columnDefs={documentColumnDefs}
                    defaultColDef={documentDefaultColDef}
                    rowHeight={50}
                    pagination={true}
                    paginationPageSize={10}
                    animateRows={true}
                    rowSelection="single"
                  />
                </Box>
              ) : (
                <Text color="gray.500" fontStyle="italic">
                  No documents added for this contract.
                </Text>
              )}
            </TabPanel>

            {/* Semaphores */}
            <TabPanel>
              {Array.isArray(form?.semaphores) && form.semaphores.length > 0 ? (
                <Box overflowX="auto" mt={4}>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th whiteSpace="nowrap" w="1%">
                          Type
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Value
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {form.semaphores.map((sem, index) => (
                        <Tr key={index}>
                          <Td whiteSpace="nowrap" w="1%">
                            <CodexLookupText
                              label=""
                              version="weirddata_v1"
                              codelist="MRCAI002"
                              codeValue={sem.type}
                            />
                          </Td>
                          <Td whiteSpace="nowrap" w="1%">
                            <CodexLookupText
                              label=""
                              version="weirddata_v1"
                              codelist="JVRI018"
                              codeValue={sem.value}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500" fontStyle="italic">
                  No semaphores defined for this contract.
                </Text>
              )}
            </TabPanel>

            <TabPanel></TabPanel>
            <TabPanel></TabPanel>
            <TabPanel></TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {hasRemoteUpdate && isDirty && (
        <Box
          mt={3}
          p={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="yellow.300"
          bg="yellow.50">
          <HStack justify="space-between">
            <Text fontSize="sm">
              Newer version available on the server. Save your changes or
              discard to refresh.
            </Text>
            <HStack>
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  setForm(serverSnapshot);
                  setIsDirty(false);
                  setHasRemoteUpdate(false);
                }}>
                Discard & Refresh
              </Button>
              <Button size="xs" colorScheme="linkedin" onClick={handleSubmit}>
                Save Now
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Template Selection Modal */}
      <Modal
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setSelectedTemplateId(null);
        }}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select a Document Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2}>
              {productTemplates.length > 0 ? (
                productTemplates.map((template) => (
                  <HStack
                    key={template._id}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    justify="space-between"
                    bg={selectedTemplateId === template._id ? "blue.50" : "white"}
                    borderColor={selectedTemplateId === template._id ? "blue.400" : "gray.200"}
                    cursor="pointer"
                    onClick={() => setSelectedTemplateId(template._id)}
                    _hover={{ borderColor: "blue.300", bg: "blue.25" }}
                  >
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold">{template.name || "Unnamed Template"}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {template.definitionName || "No type specified"}
                      </Text>
                    </VStack>
                    {selectedTemplateId === template._id && (
                      <Text color="blue.600" fontWeight="bold">✓</Text>
                    )}
                  </HStack>
                ))
              ) : (
                <Text color="gray.500" textAlign="center" py={4}>
                  No templates available for this product
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setIsTemplateModalOpen(false);
                setSelectedTemplateId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateFromTemplate}
              isDisabled={!selectedTemplateId}
            >
              Create from Template
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setDocumentToDelete(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Document
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove this document from the contract?
              {documentToDelete?.doc?.documentRepositoryId && (
                <Text mt={2} fontSize="sm" color="gray.600">
                  Document: {docRepoCache[documentToDelete.doc.documentRepositoryId]?.name || "Unnamed"}
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDocumentToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteDocument} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ContractInstanceEditor;
